import API from '@dotcom/api-middleware';
import { queryOptions } from '@tanstack/react-query';
import { digitalAddresGetResponse, saveDigitalAddressResponse } from '../data/address-response';
import {
  DigitalRecipientRequest,
  SaveAssetResponse,
  AssetFormData,
  GetDigitalAddress,
  SaveDigitalAddress,
} from '../global-types/addresses';
import { getAccountIdFromCookie } from '../utils/utility/get-account-id-from-cookie';
import { processResponse } from './middleware';
import { ErrorResponse } from '../global-types/services';

const PODService = new API();
const mockResponse = process.env.REACT_APP_MOCK_INITIALIZATION_SERVICE === 'true';

/**
 * Get all recipients associated with a project id and account id
 *
 * @param projectId - ProjectID associated with the card
 * @returns void
 */
export const getRecipients = (projectId: string, accountId = getAccountIdFromCookie()): Promise<GetDigitalAddress> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(digitalAddresGetResponse);
      return;
    }
    const response = PODService.get('/customization', `/${accountId}/${projectId}/recipients`);
    processResponse(response, resolve, reject);
  });
};

export const recipientsQueryOptions = (projectId: string) => {
  const accountId = getAccountIdFromCookie();
  return queryOptions<GetDigitalAddress, ErrorResponse>({
    queryKey: ['customization', accountId, projectId, 'recipients'],
    queryFn: () => getRecipients(projectId, accountId),
    staleTime: (query) => (Array.isArray(query.state.data?.data) || query.state.data?.data === null ? Infinity : 0),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Create and associate a recipient to a project id and account id
 *
 * @param projectId - ProjectID associated with the card
 * @param address - Address data
 * @returns void
 */
export const saveRecipient = (projectId: string, address: DigitalRecipientRequest): Promise<SaveDigitalAddress> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(saveDigitalAddressResponse);
      return;
    }
    const response = PODService.post('/customization', `/${accountId}/${projectId}/recipients`, address);
    processResponse(response, resolve, reject);
  });
};

/**
 * Updates an existing recipient
 *
 * @param projectId - ProjectID associated with the card
 * @param addressId - AddressID associated with the address
 * @param address - Address data
 * @returns void
 */
export const updateRecipient = (
  projectId: string,
  addressId: string,
  address: DigitalRecipientRequest,
): Promise<SaveDigitalAddress> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(saveDigitalAddressResponse);
      return;
    }
    const response = PODService.put('/customization', `/${accountId}/${projectId}/recipients/${addressId}`, address);
    processResponse(response, resolve, reject);
  });
};

/**
 * Saves recipient-specific assets associated with a project.
 *
 * @param projectId
 * @param recipientId
 * @param assetsUrl
 * @returns
 */
export const saveRecipientAssets = (
  projectId: string,
  assetsUrl: AssetFormData,
  recipientId = sessionStorage.getItem('recipient_id'),
): Promise<SaveAssetResponse> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(saveDigitalAddressResponse);
      return;
    }
    const response = PODService.post(
      '/customization',
      `/${accountId}/${projectId}/recipients/${recipientId}/assets`,
      assetsUrl,
    );
    processResponse(response, resolve, reject);
  });
};
